import { store } from '@redux/store';
import modalService from 'layout/services/modalService';
import { userProfileActions } from 'features/userProfile';
import { closeModalSvg } from '../constants/svgIcons';
import { privacyPolicyParagraph } from '../constants/sharedHtml';
import { FIELD_CLASS_NAMES, SELECT_FIELD_CLASS_NAMES } from '../constants/fieldClassNames';
import leadConstructor from '../services/leadConstructor';
import { postLeadInformationForm } from '../services/taskFlow';
import {
  trackNotifyFormOpened,
  recordFieldInteraction,
  recordValidationErrors,
  trackNotifySubmitClicked,
  trackNotifySubmissionFailed,
  trackNotifyModalClosed,
} from '../services/getOnTheListTracking';
import NameInput from './nameInput';
import EmailInput from './emailInput';
import PhoneInput from './phoneInput';
import ZipInput from './zipInput';
import RealtorDropdown from './realtorDropdown';
import ContactMethodDropdown from './contactMethodDropdown';
import LegalText from './legalText';

const FIELD_NAME_BY_WRAPPER_KEY = {
  fullName: 'name',
  email: 'email',
  phone: 'phone',
  zip: 'postalCode',
  contactMethod: 'preferredContactMethod',
  realtor: 'realtorQuestion',
};

/**
 * "Get on the list" lead form: same field set, validation, and submission flow as
 * GetMoreInformationForm, with its own copy ("Sign Me Up" header, "Get on the list" CTA) and no
 * back button — closing is the only way out.
 *
 * Opens directly from a single data-lead-generator-opener click (same wiring as
 * InformationFormPaid/BrochureFormPaid), unlike GetMoreInformationForm/RequestATourForm, which are
 * only ever constructed from a second click inside RequestInformationEntry. That distinction
 * matters here: wrapper.js constructs the generator for a data-lead-generator-opener click before
 * it dispatches the click's id/type/community context into Redux (services/taskFlow.js's
 * initialState runs after construction), so reading that context via selectModal(state) at
 * construction time would still see the previous value. Reading it from the constructor's own
 * `properties` argument — the clicked element's dataset, passed in by whatever registers this
 * class as a lead generator — sidesteps that ordering entirely.
 * @param {Object} properties - The clicked opener's dataset (id, type, fromPage, communityId,
 *   communityName, marketId, marketName, brandName).
 */
export default class GetOnTheListForm {
  constructor(properties = {}) {
    const state = store.getState();
    const userProfile = state?.userProfile || {};
    const {
      id, guid, type, communityId, communityName, marketId, marketName, brandName, fromPage,
    } = properties;

    this.id = id;
    this.guid = guid;
    this.type = type;
    this.communityId = communityId;
    this.communityName = communityName;
    this.marketId = marketId;
    this.marketName = marketName;
    this.brandName = brandName;
    this.fromPage = fromPage;
    this.excludeRecommendations = true;
    this.formId = 'getOnTheListForm';
    this.submitButtonId = 'getOnTheListSubmitButton';

    this.fullNameInput = new NameInput();
    this.fullNameInput.value = userProfile.name;
    this.fullNameInput
      .setClassNames(FIELD_CLASS_NAMES)
      .setLabelText('First and last name')
      .setInputId('getOnTheListName')
      .setErrorMessageId('nhs_ErrorForGetOnTheListName');

    this.emailAddressInput = new EmailInput();
    this.emailAddressInput.value = userProfile.email;
    this.emailAddressInput
      .setClassNames(FIELD_CLASS_NAMES)
      .setLabelText('Email address')
      .setInputId('getOnTheListEmail')
      .setErrorMessageId('nhs_ErrorForGetOnTheListEmail');

    this.phoneNumberInput = new PhoneInput();
    this.phoneNumberInput.value = userProfile.phone;
    this.phoneNumberInput
      .setClassNames(FIELD_CLASS_NAMES)
      .setLabelText('Phone number')
      .setInputId('getOnTheListPhone')
      .setErrorMessageId('nhs_ErrorForGetOnTheListPhone');

    this.postalCodeInput = new ZipInput();
    this.postalCodeInput.value = userProfile.zip;
    this.postalCodeInput.isInternational = userProfile.livesOutside;
    this.postalCodeInput
      .setClassNames({
        ...FIELD_CLASS_NAMES,
        checkboxWrapperClassName: 'nhs-c-input__container--with-text',
        checkboxClassName: 'nhs-c-input__check-box',
        checkboxLabelClassName: '',
      })
      .setLabelText('Your ZIP code')
      .setErrorMessageId('nhs_ErrorForGetOnTheListZip');

    this.contactMethodDropdown = new ContactMethodDropdown();
    this.contactMethodDropdown.value = userProfile.preferredContactMethod;
    this.contactMethodDropdown.setClassNames(SELECT_FIELD_CLASS_NAMES);

    this.realtorDropdown = new RealtorDropdown();
    this.realtorDropdown.value = userProfile.realtorQuestion;
    this.realtorDropdown
      .setClassNames(SELECT_FIELD_CLASS_NAMES)
      .setInputId('getOnTheListRealAgent');

    this.legalText = new LegalText(privacyPolicyParagraph, [
      { key: '{0}', value: 'Get on the list' },
      { key: '{1}', value: this.brandName ?? 'the builder' },
    ]);
    this.legalText.setId('getOnTheListLegalText');
    this.legalText.setTextClassName('nhs-o-typography--sans-label-1-extra-small-regular');

    this.onSubmitClick = null;
  }

  /**
   * Gets the form container.
   * @returns {HTMLElement}
   */
  getForm() {
    return document.getElementById(this.formId);
  }

  getSubmitButton() {
    return document.getElementById(this.submitButtonId);
  }

  static closeModal(event) {
    event.preventDefault();
    modalService.clearHtml();
  }

  /**
   * Validates every field, then builds and posts the lead. Same lead type ("information") and
   * field set as GetMoreInformationForm.submitForm, different trigger context.
   */
  async submitForm() {
    const fieldValidations = [
      { input: this.fullNameInput, wrapperKey: 'fullName' },
      { input: this.emailAddressInput, wrapperKey: 'email' },
      { input: this.phoneNumberInput, wrapperKey: 'phone' },
      { input: this.postalCodeInput, wrapperKey: 'zip' },
      { input: this.contactMethodDropdown, wrapperKey: 'contactMethod' },
      { input: this.realtorDropdown, wrapperKey: 'realtor' },
    ];

    const submitButton = this.getSubmitButton();
    submitButton.disabled = true;

    const results = await Promise.all(fieldValidations.map(({ input }) => input.validate()));

    fieldValidations.forEach(({ wrapperKey }, index) => {
      const wrapper = this.getForm().querySelector(`[data-field-wrapper="${wrapperKey}"]`);
      wrapper?.classList.toggle('nhs-c-input__wrapper--error', !results[index]);
    });

    const invalidFieldNames = fieldValidations
      .filter((_, index) => !results[index])
      .map(({ wrapperKey }) => FIELD_NAME_BY_WRAPPER_KEY[wrapperKey]);
    recordValidationErrors(invalidFieldNames);

    if (results.some((isValid) => !isValid)) {
      submitButton.disabled = false;
      trackNotifySubmissionFailed('validation');
      return;
    }

    // Only a validated lead is worth persisting for prefill — see bindChangeEvents() for why this
    // isn't dispatched on every field change instead.
    document.body.dispatchEvent(new CustomEvent('user-data-changed', {
      detail: { form: this.getForm() },
    }));

    const formData = new FormData(this.getForm());
    const leadInformation = Object.fromEntries(formData.entries());

    leadInformation.id = this.id;
    leadInformation.type = `${this.type}-information`;
    leadInformation.excludeRecommendations = this.excludeRecommendations;
    leadInformation.fromPage = this.fromPage;
    leadInformation.guid = this.guid;
    // Marks this lead for the Coming Soon interest list server-side — this form's trigger context
    // doesn't necessarily match the community's own current MarketingStatus, which is the only
    // other thing FreeBrochureController.WrapLeadGeneration checks. This is what makes it prepend
    // the "COMING SOON LEAD!" comment and suppress the builder notification email, the same way it
    // already does for other Coming Soon lead paths.
    leadInformation.isAddMeToInterestList = true;

    const leadObject = leadConstructor(leadInformation);
    leadObject?.setHousingInformation?.({
      communityId: this.communityId,
      communityName: this.communityName,
      marketId: this.marketId,
      marketName: this.marketName,
      brandName: this.brandName,
    });

    trackNotifySubmitClicked({
      preferredContactMethod: leadInformation.preferredContactMethod,
      realtorQuestion: leadInformation.realtorQuestion,
    });

    postLeadInformationForm(leadObject);
  }

  /**
   * Marks the phone field as required when the user's preferred contact method needs a phone
   * number to act on (Phone or Text Message) — same rule GetMoreInformationForm applies.
   * @param {string} value
   */
  setRequiredAttributeInPhone(value) {
    if (value === 'Phone' || value === 'Text Message') {
      this.phoneNumberInput.setRequired();
      return;
    }

    this.phoneNumberInput.removeRequired();
  }

  /**
   * Binds the events needed so the form can respond to user interactions.
   */
  bindEvents() {
    trackNotifyFormOpened({ fromPage: this.fromPage });

    const closeElements = document.querySelectorAll('[data-close]');
    this.onCloseClick = (event) => {
      trackNotifyModalClosed({ closeTarget: 'x_button', formElement: this.getForm() });
      GetOnTheListForm.closeModal(event);
    };
    closeElements.forEach((element) => element.addEventListener('click', this.onCloseClick));

    this.onFieldInteraction = (event) => {
      const fieldName = event.target?.name;
      if (fieldName) {
        recordFieldInteraction(fieldName);
      }
    };
    this.getForm().addEventListener('focusin', this.onFieldInteraction);

    this.onEscapeKeydown = (event) => {
      if (event.key === 'Escape') {
        trackNotifyModalClosed({ closeTarget: 'escape', formElement: this.getForm() });
      }
    };
    document.addEventListener('keydown', this.onEscapeKeydown);

    this.onOutsideClick = (event) => {
      if (event.target?.id === 'overlayElement') {
        trackNotifyModalClosed({ closeTarget: 'outside_click', formElement: this.getForm() });
      }
    };
    document.addEventListener('click', this.onOutsideClick, true);

    this.phoneNumberInput.bindEvents();
    this.postalCodeInput.bindEvents();
    this.legalText.bindEvents();
    this.realtorDropdown.setSelectedValue();
    this.contactMethodDropdown.setSelectedValue();

    this.setRequiredAttributeInPhone(this.contactMethodDropdown.value);
    this.contactMethodDropdown.getOnChangeEvent().subscribe(({ preferredContactMethod }) => {
      this.setRequiredAttributeInPhone(preferredContactMethod);
    });

    if (this.fullNameInput.value === '') {
      this.fullNameInput.focus();
    }

    this.onSubmitClick = this.submitForm.bind(this);
    this.getSubmitButton().addEventListener('click', this.onSubmitClick);

    this.bindChangeEvents();
  }

  /**
   * bindChangeEvents: subscribes every input's OnChangeEvent so any field change dispatches its
   * value into userProfileActions.update, matching GetMoreInformationForm — this is what the
   * constructor's userProfile.preferredContactMethod/realtorQuestion reads back from on reopen.
   */
  bindChangeEvents() {
    const changeInputs = [
      this.emailAddressInput,
      this.fullNameInput,
      this.postalCodeInput,
      this.phoneNumberInput,
      this.contactMethodDropdown,
      this.realtorDropdown,
    ];

    changeInputs.forEach((input) => input.getOnChangeEvent().subscribe((data) => {
      store.dispatch(userProfileActions.update(data));
    }));
  }

  /**
   * Removes the bindings that should not be still using.
   */
  unbindEvents() {
    const closeElements = document.querySelectorAll('[data-close]');
    closeElements.forEach((element) => element.removeEventListener('click', this.onCloseClick));
    this.getForm()?.removeEventListener('focusin', this.onFieldInteraction);
    document.removeEventListener('keydown', this.onEscapeKeydown);
    document.removeEventListener('click', this.onOutsideClick, true);
    this.getSubmitButton()?.removeEventListener('click', this.onSubmitClick);
    this.legalText.unbindEvents();
  }

  /**
   * @returns the HTML that represents the "Get on the list" form.
   */
  getHtml() {
    return `
    <div id="${this.formId}-wrapper" data-lead-form>
      <div class="nhs-o-flex-row-items-center nhs-u-gap--200 nhs-u-padding--600">
        <p class="nhs-u-flex--1-0-0 nhs-o-typography--souvenir-heading-1-extra-large-light">Sign Me Up</p>
        <button type="button" class="nhs-n1-c-button__modal-close nhs-n1-c-button__modal-close--transparent" data-close aria-label="Close modal">
          ${closeModalSvg}
        </button>
      </div>
      <div class="nhs-o-flex-column-items-start-self-stretch nhs-u-gap--600 nhs-u-padding--0-600-600">
        <p class="nhs-u-width--full nhs-o-typography--sans-body-1-medium-regular">
          Contact the builder to find out more about availability timing, available plans, pricing details, purchase process, and more.
        </p>
        <form id="${this.formId}" class="nhs-u-width--full">
          <input type="hidden" id="leadAction" name="leadAction" value="ContactBuilderFromISM" />
          <div class="nhs-o-flex-column-items-start-self-stretch nhs-u-gap--600">
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="fullName">${this.fullNameInput.getHtml()}</div>
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="email">${this.emailAddressInput.getHtml()}</div>
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="phone">${this.phoneNumberInput.getHtml()}</div>
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="zip">${this.postalCodeInput.getFieldHtml()}</div>
            ${this.postalCodeInput.getCheckboxHtml()}
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="contactMethod">${this.contactMethodDropdown.getHtml()}</div>
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="realtor">${this.realtorDropdown.getHtml()}</div>
          </div>
        </form>
        <button type="button" id="${this.submitButtonId}" class="nhs-c-button nhs-c-button--medium-40 nhs-c-button--primary-lead nhs-u-width--full" data-qa="get-on-the-list-button">
          Get on the list
        </button>
        ${this.legalText.getHtml()}
      </div>
    </div>`;
  }
}
